// src/components/recipes/RecipesList.js
import React, { useState, useEffect, useCallback } from "react";
import "../../static/css/App.css";
import { Container, Button, Row, Col, Modal, Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../static/css/customerTable.css";
import moment from "moment";
import { Box } from "@mui/material";
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarQuickFilter,
    GridToolbarColumnsButton,
    gridClasses,
} from "@mui/x-data-grid";
import { showConfirm } from "react-confirm-prompt";
import DynamicBreadcrumb from "../header/breadcrumb";
import recipesAPI from "../../services/recipesAPI";
import Loader from "../loader/loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import RecipeViewModal from "./RecipeViewModal";

library.add(fas);

// ✅ keep modal for future (turn ON later if backend stores reason)
const ENABLE_INACTIVE_REASON = false;

const RecipesList = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [refreshTable, setRefreshTable] = useState(false);
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 15,
    });

    const [loaderVisible, setLoaderVisible] = useState(false);

    // view modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewRecipeId, setViewRecipeId] = useState(null);

    // reason modal states (kept for future)
    const [openModal, setOpenModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [closeNotes, setCloseNotes] = useState("");
    const [recipeId, setRecipeId] = useState(null);
    const [recipeStatus, setRecipeStatus] = useState(null);
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);
    const navigate = useNavigate();

    const pathArray = [
        {
            name: "Recipes List",
            url: null,
        },
    ];

    const fetchRecipes = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;

            const data = await recipesAPI.getAllRecipes({
                limit: pageSize,
                page,
                sortModel,
                filterModel,
            });

            const records = data?.data?.records || [];
            const formattedRows = records.map((r, index) => ({
                id: r.id,
                sno: index + 1 + page * pageSize,
                createdAt: moment(r.createdAt).format("YYYY/MM/DD kk:mm:ss"),
                title: r.title,
                slug: r.slug,
                shortTitle: r.shortTitle,
                foodType: r.foodType,
                foodTypeText: Number(r.foodType) === 2 ? "Non-Veg" : "Veg",
                status: r.isActive,
            }));

            setRows(formattedRows);
            setTotalRecords(data?.data?.totalRecords || 0);
        } catch (error) {
            console.error("Failed to fetch recipes:", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel, refreshTable]);

    useEffect(() => {
        fetchRecipes();
    }, [fetchRecipes]);

    const submitUpdateStatus = async (recipeIdToUpdate, currentStatus, notes = null) => {
        try {
            setStatusUpdatingId(recipeIdToUpdate);
            const sendStatus = currentStatus === 1 ? 2 : 1;

            // ✅ Backend expects isActive in body (your API already maps it)
            const resp = await recipesAPI.updateRecipeStatus({
                recipeId: recipeIdToUpdate,
                status: sendStatus,
                // reason: notes, // keep for future if backend stores it
            });

            const message =
                resp?.message || resp?.data?.message || "Recipe status updated successfully";

            showConfirm("Success", {
                description: message,
                type: "success",
                hideCancel: true,
                confirmLabel: "OK",
            }).then(() => {
                setRefreshTable((v) => !v);
                setStatusUpdatingId(null);

                // close modal if it was opened
                if (openModal) {
                    setOpenModal(false);
                    setCloseNotes("");
                    setErrors({});
                    setRecipeId(null);
                    setRecipeStatus(null);
                }
            });
        } catch (error) {
            showConfirm("Oops!", {
                description: "Process failed, kindly try again later!",
                type: "warning",
                hideCancel: true,
                confirmLabel: "OK",
            }).then(() => {
                if (openModal) setOpenModal(false);
                setStatusUpdatingId(null);
            });
        }
    };

    const handleStatusClick = (id, status) => {
        if (statusUpdatingId === id) return;
        const sureTxt =
            status === 1
                ? "Are you sure you want to mark this recipe as inactive?"
                : "Are you sure you want to mark this recipe as active?";

        const decTxt =
            status === 1
                ? "This will hide the recipe from app users."
                : "This will show the recipe to app users.";

        showConfirm(sureTxt, {
            description: decTxt,
            type: "info",
        }).then((answer) => {
            if (!answer) return;

            // ✅ reason modal only if enabled + going to inactive
            if (ENABLE_INACTIVE_REASON && status === 1) {
                setRecipeId(id);
                setRecipeStatus(status);
                setOpenModal(true);
                return;
            }

            // ✅ normal toggle
            submitUpdateStatus(id, status);
        });
    };

    const handleEditClick = async (id) => {
        setLoaderVisible(true);
        if (id) navigate(`/admin/recipes/add?recipeId=${id}`);
        setLoaderVisible(false);
    };

    const handleViewClick = (id) => {
        if (!id) return;
        setViewRecipeId(id);
        setViewModalOpen(true);
    };

    // Reason modal validation (kept for future)
    const validateCancelForm = () => {
        const newErrors = {};
        if (!closeNotes.trim()) newErrors.closeNotes = "Notes is required";
        return newErrors;
    };

    const handleInactivate = async (event) => {
        event.preventDefault();

        const formErrors = validateCancelForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setErrors({});
        await submitUpdateStatus(recipeId, recipeStatus, closeNotes);
    };

    const handleClose = () => {
        setOpenModal(false);
        setCloseNotes("");
        setErrors({});
        setRecipeId(null);
        setRecipeStatus(null);
    };

    const handleAddClick = () => {
        navigate("/admin/recipes/add");
    };

    const columns = [
        { field: "sno", headerName: "S NO", sortable: false, width: 80 },
        { field: "createdAt", headerName: "Created ON", flex: 1 },
        { field: "title", headerName: "Recipe Name", flex: 1.5 },
        { field: "slug", headerName: "Slug", flex: 1, sortable: false },
        { field: "foodTypeText", headerName: "Type", flex: 1 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <Row>
                    <Col className={"ms-2 p-2 me-2 d-grid gap-2"}>
                        <button
                            onClick={() => handleStatusClick(params.id, parseInt(params.value, 10))}
                            disabled={statusUpdatingId === params.id}
                            className={`btn ${params.value === 1 ? "btn-danger" : "btn-secondary"} custom-status-button`}
                            style={{
                                outline: "none",
                                boxShadow: "none",
                                opacity: statusUpdatingId === params.id ? 0.6 : 1,
                                cursor: statusUpdatingId === params.id ? "not-allowed" : "pointer",
                            }}
                        >
                            {statusUpdatingId === params.id ? "Updating..." : (params.value === 1 ? "Active" : "Inactive")}
                        </button>
                    </Col>
                </Row>
            ),
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Row>
                    <Col className={"ms-2 p-2 me-2 d-grid gap-2"}>
                        <button
                            onClick={() => handleEditClick(params.id)}
                            className="btn btn-success"
                            style={{ outline: "none", boxShadow: "none" }}
                        >
                            Edit
                        </button>

                        <Button
                            className={"p-2 me-2 mb-1 "}
                            size="sm"
                            onClick={() => handleViewClick(params.id)}
                        >
                            <FontAwesomeIcon
                                icon="fa-solid fa-eye"
                                size="sm"
                                style={{ color: "black" }}
                            />{" "}
                            View
                        </Button>
                    </Col>
                </Row>
            ),
        },
    ];

    const CustomToolbar = () => (
        <GridToolbarContainer sx={{ justifyContent: "space-between", p: 1 }}>
            <GridToolbarColumnsButton />   {/* ✅ Column show/hide */}
            <GridToolbarQuickFilter />     {/* ✅ Search */}
        </GridToolbarContainer>
    );

    return (
        <Container className="mt-4 mb-4">
            <Box className="d-flex justify-content-between align-items-center">
                <DynamicBreadcrumb pathArray={pathArray} classesName="col-4" />

                <button className="btn btn-secondary mx-3  w-auto flex-shrink-0" onClick={handleAddClick}>
                    <FontAwesomeIcon icon="fa-solid fa-bowl-food" className="me-2" />
                    Add New Recipe
                </button>
            </Box>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>ALL Recipes</h5>
            </div>

            {loaderVisible && <Loader />}

            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    rowsPerPageOptions={[5, 10, 20]}
                    paginationMode="server"
                    rowCount={totalRecords}
                    loading={loading}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortingMode="server"
                    onSortModelChange={setSortModel}
                    autosizeOptions={{
                        columns: ["title", "slug", "status"],
                        includeOutliers: true,
                        includeHeaders: false,
                    }}
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                    sx={{
                        [`& .${gridClasses.cell}`]: { py: 0.5 },
                    }}
                />
            </Box>

            {/* ✅ Reason modal kept for future use */}
            {ENABLE_INACTIVE_REASON && (
                <Modal show={openModal} onHide={handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>Enter Reason</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <Form className="cancelForm">
                            <Form.Group className="mb-3" controlId="closeNotes">
                                <Form.Label>Enter inactivate reason</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={closeNotes}
                                    onChange={(e) => setCloseNotes(e.target.value)}
                                    isInvalid={!!errors.closeNotes}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.closeNotes}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Form>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="primary" onClick={handleInactivate}>
                            Submit Reason
                        </Button>
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {viewRecipeId && (
                <RecipeViewModal
                    recipeId={viewRecipeId}
                    show={viewModalOpen}
                    onHide={() => {
                        setViewModalOpen(false);
                        setViewRecipeId(null);
                    }}
                />
            )}
        </Container>
    );
};

export default RecipesList;
